import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { Search, ShoppingCart, Power, PowerOff, User, Tv, History as HistoryIcon, X, Package, RefreshCw, PlusCircle, Eye } from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';
import { useViewMode } from '../hooks/useViewMode';
import { Screen, PageHead, Panel, Field, Notice, Pill, Tabs, Details, Item, Empty } from '../components/gtpl/GtplKit';

const NO_RIGHT = (what) => <span className="gx-hint" style={{ color: 'var(--g-bad)', margin: 0 }}>You don't have permission to {what}.</span>;

function ResultBanner({ result, ok = 'ok', okLabel = 'Submitted', failLabel = 'Failed' }) {
  if (!result) return null;
  const failed = result[ok] === false;
  return (
    <Notice tone={failed ? 'bad' : 'ok'} title={failed ? failLabel : okLabel}>
      {result.message || null}
    </Notice>
  );
}

// ─── GTPL Saathi ("Order") ────────────────────────────────────────────────────
// A genuinely NEW package order — distinct from Renew above, which only extends an
// EXISTING package. GTPL itself rejects this for an STB/account that already has an
// active base pack (surfaces on Search below). Real money changes hands on Place Order —
// the LCO's own GTPL wallet gets debited — so this is a deliberate two-step flow: Preview
// shows GTPL's own pricing without spending anything, and only then does Place Order
// become available. Packages/broadcasters/channels are never hardcoded — scraped live from
// GTPL on every Search/Preview. Ported from CustomerDetailPage.js's GtplOrderPanel (the
// per-customer "Apply Order" tab) — same logic, just driven by this page's own stb/account
// fields instead of a specific customer's record.
const emptyOrderSelection = { setupType: 'M', validity: 'D30', language: '', packageName: '', packageCode: '', ftaBase: '', broadcasterNames: [], channelNames: [] };

// A tappable choice row (radio / checkbox) with a highlighted selected state.
function Choice({ type = 'radio', checked, onChange, children, right }) {
  return (
    <label className={`gx-item${checked ? ' is-selected' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '9px 12px' }}>
      <input type={type} checked={checked} onChange={onChange} />
      <span style={{ flex: 1, fontSize: '.88rem' }}>{children}</span>
      {right}
    </label>
  );
}

function GtplOrderPanel({ stb, accountNumber, canPlaceOrder }) {
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [options, setOptions] = useState(null); // orderSearch() result
  const [sel, setSel] = useState(emptyOrderSelection);

  const [channelFilters, setChannelFilters] = useState({ genre: '', language: '', sdhd: '', search: '' });
  const [channelResults, setChannelResults] = useState(null);
  const [channelsLoading, setChannelsLoading] = useState(false);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');

  const [placing, setPlacing] = useState(false);
  const [placeResult, setPlaceResult] = useState(null);

  const resetDownstream = () => { setPreview(null); setPreviewError(''); setPlaceResult(null); };

  const handleSearch = async () => {
    if (!stb.trim() && !accountNumber.trim()) { toast.error('Enter an STB serial or account number'); return; }
    setSearching(true);
    setSearchError('');
    setOptions(null);
    setSel(emptyOrderSelection);
    resetDownstream();
    setChannelResults(null);
    try {
      const { data } = await gtplSaathiAPI.orderSearch({ stb: stb.trim() || undefined, accountNumber: accountNumber.trim() || undefined });
      setOptions(data.data);
    } catch (err) {
      setSearchError(err.response?.data?.message || 'Order search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleLoadChannels = async () => {
    setChannelsLoading(true);
    try {
      const { data } = await gtplSaathiAPI.orderChannelSearch({
        stb: stb.trim() || undefined, accountNumber: accountNumber.trim() || undefined,
        genre: channelFilters.genre || undefined, language: channelFilters.language || undefined,
        sdhd: channelFilters.sdhd || undefined, search: channelFilters.search || undefined,
      });
      setChannelResults(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Channel search failed');
    } finally {
      setChannelsLoading(false);
    }
  };

  const toggleBroadcaster = (label) => setSel((s) => ({
    ...s,
    broadcasterNames: s.broadcasterNames.includes(label) ? s.broadcasterNames.filter((n) => n !== label) : [...s.broadcasterNames, label],
  }));
  const toggleChannel = (label) => setSel((s) => ({
    ...s,
    channelNames: s.channelNames.includes(label) ? s.channelNames.filter((n) => n !== label) : [...s.channelNames, label],
  }));

  const buildOrderParams = () => ({
    stb: stb.trim() || undefined, accountNumber: accountNumber.trim() || undefined,
    setupType: sel.setupType, validity: sel.validity, language: sel.language || undefined,
    packageName: sel.setupType === 'M' ? sel.packageName : undefined,
    packageCode: sel.setupType === 'M' ? sel.packageCode : undefined,
    ftaBase: sel.setupType !== 'M' ? sel.ftaBase : undefined,
    broadcasterNames: sel.setupType === 'O' ? sel.broadcasterNames : undefined,
    channelNames: sel.setupType === 'A' ? sel.channelNames : undefined,
  });

  const handlePreview = async () => {
    if (sel.setupType === 'M' && !sel.packageCode) { toast.error('Select a package'); return; }
    if (sel.setupType !== 'M' && !sel.ftaBase) { toast.error('Select an FTA base'); return; }
    if (sel.setupType === 'O' && !sel.broadcasterNames.length) { toast.error('Select at least one broadcaster'); return; }
    if (sel.setupType === 'A' && !sel.channelNames.length) { toast.error('Select at least one channel'); return; }
    setPreviewing(true);
    setPreviewError('');
    setPlaceResult(null);
    try {
      const { data } = await gtplSaathiAPI.orderPreview(buildOrderParams());
      setPreview(data.data);
    } catch (err) {
      setPreviewError(err.response?.data?.message || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handlePlace = async () => {
    setPlacing(true);
    setPlaceResult(null);
    try {
      const { data } = await gtplSaathiAPI.placeGtplOrder(buildOrderParams());
      setPlaceResult({ ok: true, ...data.data });
      toast.success('Order placed on GTPL Saathi');
    } catch (err) {
      const message = err.response?.data?.message || 'Place order failed';
      setPlaceResult({ ok: false, message });
      toast.error(message);
    } finally {
      setPlacing(false);
    }
  };

  return (
    <>
      <Notice tone="warn" title="This spends real money">
        Places a genuinely NEW package order — for an STB/account with no active base pack yet.
        GTPL rejects this outright if one already exists (use Renew instead). The cost comes out of the GTPL wallet,
        so always Preview first. Uses the STB serial / account number typed at the top.
      </Notice>

      <Panel icon={PlusCircle} tone="adv" title="Place a new order">
        <div className="gx-actions">
          <button type="button" className="btn btn-secondary" onClick={handleSearch} disabled={searching || !canPlaceOrder}>
            <Search size={15} />{searching ? 'Searching…' : 'Find order options'}
          </button>
          {!canPlaceOrder && NO_RIGHT('place GTPL Saathi orders')}
        </div>
        {searchError && <div style={{ marginTop: 12 }}><Notice tone="bad">{searchError}</Notice></div>}

        {options && (
          <div className="gx-form" style={{ marginTop: 14 }}>
            <Notice tone="info" icon={User} title={options.customer.name}>
              {options.customer.entity}{' · '}
              <Pill tone={options.customer.status === 'INACTIVE' ? 'warn' : 'ok'} dot>{options.customer.status}</Pill>
              {options.customer.lastAssignedPackage && <> · Last package: {options.customer.lastAssignedPackage}</>}
            </Notice>

            <Field label="Setup type">
              <Tabs value={sel.setupType} onChange={(val) => { setSel((s) => ({ ...emptyOrderSelection, validity: s.validity, language: s.language, setupType: val })); resetDownstream(); }}
                tabs={[{ key: 'M', label: 'GTPL pack' }, { key: 'O', label: 'Broadcaster bouquet' }, { key: 'A', label: 'A la carte' }]} />
            </Field>

            <div className="gx-grid">
              <Field label="Validity">
                <select className="form-control" value={sel.validity} onChange={(e) => { setSel((s) => ({ ...s, validity: e.target.value })); resetDownstream(); }}>
                  {options.validityOptions.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
                </select>
              </Field>
              <Field label="Language (optional)">
                <select className="form-control" value={sel.language} onChange={(e) => { setSel((s) => ({ ...s, language: e.target.value })); resetDownstream(); }}>
                  <option value="">GTPL default</option>
                  {options.languages.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </Field>
            </div>

            {sel.setupType === 'M' && (
              <Field label="Package">
                <div className="gx-list">
                  {options.recommendedPackages.map((p) => (
                    <Choice key={p.id} checked={sel.packageCode === p.code}
                      onChange={() => { setSel((s) => ({ ...s, packageName: p.label, packageCode: p.code })); resetDownstream(); }}>
                      {p.label}
                    </Choice>
                  ))}
                </div>
              </Field>
            )}

            {sel.setupType !== 'M' && (
              <Field label="FTA base (required)">
                <div className="gx-actions">
                  {options.ftaBaseOptions.map((f) => (
                    <button key={f.id} type="button" aria-pressed={sel.ftaBase === f.code}
                      className={`btn btn-sm ${sel.ftaBase === f.code ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => { setSel((s) => ({ ...s, ftaBase: f.code })); resetDownstream(); }}>{f.code}</button>
                  ))}
                </div>
              </Field>
            )}

            {sel.setupType === 'O' && (
              <Field label={`Broadcasters (${sel.broadcasterNames.length} selected)`}>
                <div className="gx-list" style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {options.broadcasters.map((b) => (
                    <Choice key={b.id} type="checkbox" checked={sel.broadcasterNames.includes(b.label)}
                      onChange={() => { toggleBroadcaster(b.label); resetDownstream(); }}>{b.label}</Choice>
                  ))}
                </div>
              </Field>
            )}

            {sel.setupType === 'A' && (
              <Field label="Channels">
                <div className="gx-grid">
                  <input className="form-control" placeholder="Search channel…" value={channelFilters.search} onChange={(e) => setChannelFilters((f) => ({ ...f, search: e.target.value }))} />
                  {channelResults?.filterOptions?.genres?.length > 0 && (
                    <select className="form-control" value={channelFilters.genre} onChange={(e) => setChannelFilters((f) => ({ ...f, genre: e.target.value }))}>
                      {channelResults.filterOptions.genres.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                  )}
                  {channelResults?.filterOptions?.languages?.length > 0 && (
                    <select className="form-control" value={channelFilters.language} onChange={(e) => setChannelFilters((f) => ({ ...f, language: e.target.value }))}>
                      {channelResults.filterOptions.languages.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                  )}
                  {channelResults?.filterOptions?.sdhd?.length > 0 && (
                    <select className="form-control" value={channelFilters.sdhd} onChange={(e) => setChannelFilters((f) => ({ ...f, sdhd: e.target.value }))}>
                      {channelResults.filterOptions.sdhd.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  )}
                </div>
                <div className="gx-actions" style={{ marginTop: 8 }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleLoadChannels} disabled={channelsLoading}>
                    <RefreshCw size={14} />{channelsLoading ? 'Loading…' : 'Load channels'}
                  </button>
                </div>

                {sel.channelNames.length > 0 && (
                  <div className="gx-actions" style={{ marginTop: 10 }}>
                    {sel.channelNames.map((n) => (
                      <button key={n} type="button" className="btn btn-sm btn-outline" onClick={() => { toggleChannel(n); resetDownstream(); }}>
                        {n} <X size={12} />
                      </button>
                    ))}
                  </div>
                )}

                {channelResults && (
                  <div className="gx-list" style={{ maxHeight: 280, overflowY: 'auto', marginTop: 10 }}>
                    {channelResults.channels.length === 0 && <Empty title="No channels matched" />}
                    {channelResults.channels.map((c) => (
                      <Choice key={c.id} type="checkbox" checked={sel.channelNames.includes(c.label)}
                        onChange={() => { toggleChannel(c.label); resetDownstream(); }}
                        right={c.price != null ? <Pill tone="mute">₹{c.price}</Pill> : null}>{c.label}</Choice>
                    ))}
                  </div>
                )}
              </Field>
            )}

            <div className="gx-sticky">
              <button type="button" className="btn btn-secondary" onClick={handlePreview} disabled={previewing || !canPlaceOrder}>
                <Eye size={15} />{previewing ? 'Loading pricing…' : 'Preview price'}
              </button>
              <button type="button" className="btn btn-adv" onClick={handlePlace} disabled={placing || !preview || !canPlaceOrder}>
                <ShoppingCart size={15} />{placing ? 'Placing…' : 'Place order (spends real money)'}
              </button>
            </div>

            {previewError && <Notice tone="bad">{previewError}</Notice>}

            {preview && (
              <Notice tone="info" title="Pricing (from GTPL Saathi)">
                <Details items={[
                  ['Retail price', preview.pricing.retailPrice != null ? `₹${preview.pricing.retailPrice}` : null],
                  ['GST', preview.pricing.gst != null ? `₹${preview.pricing.gst}` : null],
                  ['Total retail price', preview.pricing.totalRetailPrice != null ? `₹${preview.pricing.totalRetailPrice}` : null],
                  ['Wallet deduction', preview.pricing.walletDeduction != null ? `₹${preview.pricing.walletDeduction}` : null],
                ]} />
                {preview.pricing.retailPrice == null && preview.pricing.walletDeduction == null && (
                  <div style={{ marginTop: 6 }}>GTPL didn't show a pricing breakdown for this selection — verify the amount on gtplsaathi.com before placing.</div>
                )}
              </Notice>
            )}

            {placeResult && (
              <Notice tone={placeResult.ok === false ? 'bad' : 'ok'} title={placeResult.ok === false ? 'Order failed' : 'Order placed'}>
                {placeResult.message}
                {placeResult.ok === false && <div style={{ marginTop: 6 }}>An admin has been notified on WhatsApp.</div>}
                {placeResult.ok !== false && <div style={{ marginTop: 6 }}>Verify this on gtplsaathi.com directly — confirm the order really landed.</div>}
              </Notice>
            )}
          </div>
        )}
      </Panel>
    </>
  );
}

// Free-standing GTPL Saathi lookup/action page — not tied to any CRM customer record,
// unlike CustomerDetailPage's "Apply Order"/Suspend-Activate tab (GtplSaathiPanel there).
// This is for looking a subscriber up directly on gtplsaathi.com by STB/account/mobile —
// e.g. a subscriber who doesn't (yet) match a CRM record, or a quick status check without
// opening a customer page at all. Same underlying endpoints (search/renew/suspend), same
// permission model (moduleRegistry's gtplSaathi.search/renew/suspendActive).
export default function GtplSaathiSubscriberPage() {
  useDarkReady();
  const { hasModuleRight } = useAuth();
  const canSearch = hasModuleRight('gtplSaathi', 'search');
  const canRenew = hasModuleRight('gtplSaathi', 'renew');
  const canSuspend = hasModuleRight('gtplSaathi', 'suspendActive');
  const canPlaceOrder = hasModuleRight('gtplSaathi', 'placeOrder');

  const [searchParams] = useSearchParams();
  const [stb, setStb] = useState(() => searchParams.get('stb') || '');
  const [accountNumber, setAccountNumber] = useState(() => searchParams.get('accountNumber') || '');
  const [mobile, setMobile] = useState('');
  const [searching, setSearching] = useState(false);
  const [subscriber, setSubscriber] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const orderPanelRef = useRef(null);

  // Renew / Suspend / New order are one-at-a-time tabs under the lookup (all views). Inactive
  // tabs are only hidden, not unmounted, so a half-built order survives a tab switch.
  const { viewMode } = useViewMode();
  const isApp = viewMode === 'app' || window.innerWidth < 640;
  const [pane, setPane] = useState(() => (searchParams.get('focus') === 'order' ? 'order' : 'renew'));
  const showPane = (p) => (pane !== p ? { display: 'none' } : null);

  const [validity, setValidity] = useState('');
  const [renewing, setRenewing] = useState(false);
  const [renewResult, setRenewResult] = useState(null);

  const [reasons, setReasons] = useState([]);
  const [suspendAction, setSuspendAction] = useState('SUSPEND');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [suspending, setSuspending] = useState(false);
  const [suspendResult, setSuspendResult] = useState(null);

  useEffect(() => {
    if (!canSuspend) return;
    gtplSaathiAPI.getSuspendReasons()
      .then(({ data }) => setReasons(data.data || []))
      .catch(() => {});
  }, [canSuspend]);

  // Arrived here from the GTPL Retry Queue's "Place Order Instead" link — jump straight to
  // the Order panel below instead of leaving staff to scroll past Renew/Suspend first.
  useEffect(() => {
    if (searchParams.get('focus') === 'order' && orderPanelRef.current) {
      orderPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const hasQuery = stb.trim() || accountNumber.trim() || mobile.trim();

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault();
    if (!hasQuery) { toast.error('Enter an STB serial, account number, or mobile'); return; }
    setSearching(true);
    setSearchError(null);
    setSubscriber(null);
    try {
      const { data } = await gtplSaathiAPI.search({
        stb: stb.trim() || undefined,
        accountNumber: accountNumber.trim() || undefined,
        mobile: mobile.trim() || undefined,
      });
      if (data.data?.found === false) {
        setSearchError(data.data.message || 'No matching subscriber found on GTPL Saathi');
      } else {
        setSubscriber(data.data);
      }
    } catch (err) {
      setSearchError(err.response?.data?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  }, [stb, accountNumber, mobile, hasQuery]);

  const handleRenew = async (e) => {
    e.preventDefault();
    if (!hasQuery) { toast.error('Enter an STB serial or account number'); return; }
    // Phone taps are easy to fumble and Renew spends GTPL wallet money, so ask once there.
    if (isApp && !window.confirm(`Renew ${stb.trim() || accountNumber.trim()} on GTPL now? This spends GTPL wallet money.`)) return;
    setRenewing(true);
    setRenewResult(null);
    try {
      const { data } = await gtplSaathiAPI.renew({
        stb: stb.trim() || undefined,
        accountNumber: accountNumber.trim() || undefined,
        validity: validity || undefined,
      });
      setRenewResult(data.data);
      if (data.data?.ok === false) toast.error('Order failed on GTPL Saathi — admin has been alerted');
      else toast.success('Order applied on GTPL Saathi');
    } catch (err) {
      const message = err.response?.data?.message || 'Apply order failed';
      setRenewResult({ ok: false, message });
      toast.error(message);
    } finally {
      setRenewing(false);
    }
  };

  const handleSuspend = async (e) => {
    e.preventDefault();
    if (!hasQuery) { toast.error('Enter an STB serial or account number'); return; }
    if (!reason) { toast.error('Select a reason'); return; }
    setSuspending(true);
    setSuspendResult(null);
    try {
      const { data } = await gtplSaathiAPI.suspendActive({
        stb: stb.trim() || undefined,
        accountNumber: accountNumber.trim() || undefined,
        action: suspendAction,
        reason,
        notes: notes.trim() || undefined,
      });
      setSuspendResult({ ok: true, message: data.data?.message });
      toast.success(suspendAction === 'ACTIVE' ? 'Activated on GTPL Saathi' : 'Suspended on GTPL Saathi');
    } catch (err) {
      const message = err.response?.data?.message || 'Suspend/Activate failed';
      setSuspendResult({ ok: false, message });
      toast.error(message);
    } finally {
      setSuspending(false);
    }
  };

  const c = subscriber?.customer;
  const addr = subscriber?.address?.line1
    ? [subscriber.address.line1, subscriber.address.city, subscriber.address.state].filter(Boolean).join(', ')
    : null;

  return (
    <Screen>
      <PageHead icon={Search} tone="info" title="GTPL subscriber lookup"
        sub="Look up, renew, or suspend/activate any GTPL subscriber by STB serial, account number or mobile — independent of CRM records. For a CRM customer, use their Apply Order tab instead; it's pre-filled." />

      <Panel icon={Search} tone="info" title="1. Find the subscriber">
        <form onSubmit={handleSearch} className="gx-form">
          <div className="gx-grid">
            <Field label="STB serial"><input className="form-control" value={stb} onChange={e => setStb(e.target.value)} placeholder="STB serial" /></Field>
            <Field label="Account number"><input className="form-control" value={accountNumber} onChange={e => setAccountNumber(e.target.value)} placeholder="GTPL account number" /></Field>
            <Field label="Mobile"><input className="form-control" inputMode="tel" value={mobile} onChange={e => setMobile(e.target.value)} placeholder="Mobile number" /></Field>
          </div>
          <div className="gx-actions">
            <button type="submit" className="btn btn-primary" disabled={searching || !canSearch}>
              <Search size={15} />{searching ? 'Searching…' : 'Search'}
            </button>
            {!canSearch && NO_RIGHT('search GTPL Saathi')}
          </div>
        </form>
        {searchError && <div style={{ marginTop: 12 }}><Notice tone="warn">{searchError}</Notice></div>}
      </Panel>

      {subscriber && (
        <>
          <Panel icon={User} tone="ok" title={c?.name || '—'} sub={c?.category}>
            <Details items={[
              ['Mobile', c?.mobile], ['Balance', c?.balance], ['KYC', c?.kyc], ['ePay', c?.ePay], ['Created', c?.createdOn],
              ['Address', addr, true],
            ]} />
          </Panel>

          {subscriber.stb?.detail && (
            <Panel icon={Tv} tone="adv" title="Set-top box" sub={subscriber.stb.detail}>
              <Details items={[
                ['Status', subscriber.stb.status], ['VC no.', subscriber.stb.vcNo], ['Auto-renew', subscriber.stb.autoRenew],
                ['Issued on', subscriber.stb.issuedOn], ['Warranty end', subscriber.stb.warrantyEnd],
              ]} />
            </Panel>
          )}

          {subscriber.packages?.length > 0 && (
            <Panel icon={Package} tone="info" title="Packages" count={subscriber.packages.length}>
              <div className="gx-list">
                {subscriber.packages.map((p, i) => (
                  <Item key={i} title={p.name} sub={p.mrp ? `₹${p.mrp}` : null}
                    right={p.status ? <Pill tone={/active/i.test(p.status) && !/in/i.test(p.status) ? 'ok' : 'mute'} dot>{p.status}</Pill> : null}
                    meta={<span>{p.startDate} → {p.endDate}</span>} />
                ))}
              </div>
            </Panel>
          )}

          {subscriber.history?.length > 0 && (
            <Panel icon={HistoryIcon} tone="mute" title="Recent history" collapsible open={false} count={Math.min(10, subscriber.history.length)}>
              <div className="gx-list">
                {subscriber.history.slice(0, 10).map((h, i) => (
                  <Item key={i} title={h.description} sub={h.date} />
                ))}
              </div>
            </Panel>
          )}
        </>
      )}

      <div style={{ marginTop: 8 }}>
        <Tabs value={pane} onChange={setPane} tabs={[
          { key: 'renew', label: 'Renew', icon: ShoppingCart },
          { key: 'suspend', label: 'Suspend / Activate', icon: PowerOff },
          { key: 'order', label: 'New order', icon: PlusCircle },
        ]} />
      </div>

      <div style={showPane('renew')}>
        <Panel icon={ShoppingCart} tone="ok" title="Renew (Apply Order)" sub="Renews the STB/account typed above directly on GTPL's own dealer portal.">
          <form onSubmit={handleRenew} className="gx-form">
            <Field label="Validity (optional)" style={{ maxWidth: 320 }}>
              <input className="form-control" value={validity} onChange={e => setValidity(e.target.value)} placeholder="Leave blank for site default" />
            </Field>
            <div className="gx-actions">
              <button type="submit" className="btn btn-success" disabled={renewing || !canRenew}>
                <ShoppingCart size={15} />{renewing ? 'Renewing…' : 'Renew'}
              </button>
              {!canRenew && NO_RIGHT('apply GTPL Saathi orders')}
            </div>
          </form>
          <ResultBanner result={renewResult} okLabel="Renewal applied" failLabel="Renewal failed" />
        </Panel>
      </div>

      <div style={showPane('suspend')}>
        <Panel icon={suspendAction === 'ACTIVE' ? Power : PowerOff} tone={suspendAction === 'ACTIVE' ? 'ok' : 'bad'} title="Suspend / Activate"
          sub="Flips status directly on GTPL Saathi for the STB/account typed above.">
          <form onSubmit={handleSuspend} className="gx-form">
            <div className="gx-grid">
              <Field label="Action">
                <select className="form-control" value={suspendAction} onChange={e => setSuspendAction(e.target.value)}>
                  <option value="SUSPEND">Suspend</option>
                  <option value="ACTIVE">Activate</option>
                </select>
              </Field>
              <Field label="Reason">
                <select className="form-control" value={reason} onChange={e => setReason(e.target.value)}>
                  <option value="">Select reason…</option>
                  {reasons.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Notes (optional)"><input className="form-control" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes" /></Field>
            </div>
            <div className="gx-actions">
              <button type="submit" className={`btn ${suspendAction === 'ACTIVE' ? 'btn-success' : 'btn-danger'}`} disabled={suspending || !canSuspend}>
                {suspendAction === 'ACTIVE' ? <Power size={15} /> : <PowerOff size={15} />}
                {suspending ? 'Submitting…' : (suspendAction === 'ACTIVE' ? 'Activate' : 'Suspend')}
              </button>
              {!canSuspend && NO_RIGHT('suspend/activate on GTPL Saathi')}
            </div>
          </form>
          <ResultBanner result={suspendResult} okLabel="Submitted" failLabel="Failed" />
        </Panel>
      </div>

      <div ref={orderPanelRef} style={showPane('order')}>
        <GtplOrderPanel stb={stb} accountNumber={accountNumber} canPlaceOrder={canPlaceOrder} />
      </div>
    </Screen>
  );
}
